import type { InputProps } from '../ui/Input/Input';
import { useCallback, useEffect, useRef, useState } from 'react';
import { minimumEventDuration } from '../core/Core';
import { hToMs, msToH } from '../../lib/time';

export function oneDecimal(number: number) {
  return Math.round(10 * number) / 10;
}

export const useUpdatingDuration = (
  overrideDuration: number | null,
  defaultDuration: number | null,
  onSubmit: (currentValue: number | null) => void,
  max: number | undefined,
): Partial<InputProps> => {
  const [updatedOverrideDuration, _setUpdatedOverrideDuration] = useState<
    number | null
  >(null);

  const hasChanged = useRef<boolean>(false);

  const setUpdatedOverrideDurationFromHours = useCallback(
    (durationH: number | null) => {
      hasChanged.current = true;
      _setUpdatedOverrideDuration(
        durationH === null
          ? null
          : Math.max(
              minimumEventDuration,
              hToMs(Math.min(max ?? Infinity, durationH)),
            ),
      );
    },
    [_setUpdatedOverrideDuration, max],
  );

  useEffect(() => {
    _setUpdatedOverrideDuration(
      overrideDuration === defaultDuration ? null : overrideDuration,
    );
  }, [overrideDuration, defaultDuration]);

  const submit = useCallback(() => {
    if (hasChanged.current) {
      onSubmit(updatedOverrideDuration);
      hasChanged.current = false;
    }
  }, [onSubmit, updatedOverrideDuration]);

  /** Relevant in particular when sea duration override is greater than allowed by the factual start time on ETA */

  const warnImpossibleOverride =
    overrideDuration !== null &&
    defaultDuration !== null &&
    Math.abs(overrideDuration - defaultDuration) > hToMs(0.5);

  return {
    value:
      updatedOverrideDuration !== null
        ? oneDecimal(msToH(updatedOverrideDuration))
        : '',
    color: warnImpossibleOverride ? 'red' : undefined,
    style: warnImpossibleOverride
      ? { textDecoration: 'line-through' }
      : undefined,
    onKeyPress: (e) => e.key === 'Enter' && submit(),
    onKeyDown: (e) => {
      const step = e.shiftKey ? 10 : 1;
      const currentValue =
        e.currentTarget.value === ''
          ? oneDecimal(msToH(defaultDuration ?? 0))
          : Number(e.currentTarget.value);
      if (e.key === 'ArrowUp') {
        setUpdatedOverrideDurationFromHours(
          e.metaKey || e.ctrlKey
            ? currentValue + 0.1
            : Math.floor(currentValue) + step,
        );
        e.preventDefault();
      } else if (e.key === 'ArrowDown') {
        setUpdatedOverrideDurationFromHours(
          e.metaKey || e.ctrlKey
            ? currentValue - 0.1
            : Math.ceil(currentValue) - step,
        );
        e.preventDefault();
      }
    },
    onChange: (e) => {
      const newDurationH = e.target.value !== '' ? +e.target.value : null;
      setUpdatedOverrideDurationFromHours(newDurationH);
    },
    onBlur: submit,
  };
};
